import { useCallback, useEffect } from "react";
import { DataMessage } from "amazon-chime-sdk-js";
import { useAudioVideo } from "amazon-chime-sdk-component-library-react";
import { useAppSelector } from "../redux/app/hooks";
import { useChimeMeeting } from "../Chime/ChimeMeetingContext";
import { ChatMessage } from "../components/Meeting/MeetingChat/types/MeetingChat.types";

const TOPIC = "chat";

export const useChimeMeetingChat = (setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>) => {
  const audioVideo = useAudioVideo();
  const { attendeeId, setHasNewChatMessages } = useChimeMeeting();

  const fromState = useAppSelector((state) => state?.auth?.adminProfile);
  const profile = localStorage.getItem('adminProfile') ? JSON.parse(localStorage.getItem('adminProfile') || '') : fromState;
  const user = profile?.getCurrentUser?.nodes?.[0];

  const userName = user?.uFullName;
  const userId = user?.uId;

  const onDataMessageReceived = useCallback(
    (dataMessage: DataMessage) => {
      try {
        const decodedText = typeof dataMessage.data === "string" ? dataMessage.data : new TextDecoder().decode(dataMessage.data);

        const parsed = JSON.parse(decodedText);

        if (dataMessage.senderAttendeeId === attendeeId) return;

        setChatMessages((prev) => [...prev, parsed]);
        setHasNewChatMessages(true);
      } catch (err) {
        console.error("Failed to parse message:", err);
      }
    },
    [attendeeId, setChatMessages, setHasNewChatMessages]
  );

  useEffect(() => {
    if (!audioVideo) return;

    audioVideo.realtimeSubscribeToReceiveDataMessage(TOPIC, onDataMessageReceived);

    return () => {
      try {
        audioVideo.realtimeUnsubscribeFromReceiveDataMessage(TOPIC);
      } catch (err) {
        console.warn("Failed to unsubscribe data message:", err);
      }
    };
  }, [audioVideo, onDataMessageReceived]);

  const sendMessage = (message: string) => {
    if (!audioVideo || !attendeeId) return;
    if (!message?.trim()) return;

    const payload = {
      userId,
      name: userName,
      message: message.trim(),
      sentAt: Date.now(),
    };

    try {
      audioVideo.realtimeSendDataMessage(TOPIC, JSON.stringify(payload), 1000);
    } catch (err) {
      console.error("Failed to send chime data message:", err);
    }

    setChatMessages((prev) => [...prev, payload]);
  };

  return { sendMessage };
};
